<template>
  <div class="scorm-player-container position-relative">

    <div v-if="showResumePrompt" class="resume-prompt-overlay">
      <div class="card p-4 text-center shadow-lg" style="max-width: 400px;">
        <h4 class="text-dark mb-3">Resume Training?</h4>
        <p class="text-muted mb-4">We detected previous progress for this module. Would you like to pick up where you left off, or start a completely new attempt?</p>
        <div class="d-flex justify-content-center gap-3">
          <button class="btn btn-outline-secondary px-4" @click="handleResumeChoice(false)">Start Over</button>
          <button class="btn bg-theme text-white px-4" @click="handleResumeChoice(true)">Resume</button>
        </div>
      </div>
    </div>

    <div v-else-if="!scormContentUrl" class="loading-state">
      <div class="spinner-border text-info" role="status"></div>
      <p class="mt-2 text-white">Initializing Scorm Module...</p>
    </div>

    <iframe
      v-else
      ref="scormIframe"
      :src="scormContentUrl"
      class="scorm-iframe"
      frameborder="0"
      allow="autoplay; fullscreen"
    ></iframe>
  </div>
</template>

<script>
export default {
  name: 'ScormPlayer',
  props: {
    assignmentId: { type: [Number, String], required: true },
    moduleId: { type: [Number, String], required: true },
    userId: { type: [Number, String], required: true },
    launchPath: { type: String, required: true }
  },

  data() {
    return {
      scormContentUrl: '',
      cmiData: {},
      lmsInitialized: false,
      alreadyMarkedComplete: false,
      // NEW STATE VARIABLES
      showResumePrompt: false,
      tempDbTrackingData: null
    };
  },

  mounted() {
    this.initPlayer();
  },

  unmounted() {
    // Clean up the global window when the player closes so it doesn't leak!
    delete window.API;
    delete window.API_1484_11;
  },
  watch: {
    assignmentId() {
      this.initPlayer();
    }
  },
  methods: {
    async initPlayer() {
      if (this.assignmentId !== 'preview') {
        await this.loadScormTrackingData();
      } else {
        this.launchScormIframe(); // Skip tracking for previews
      }
    },

    async markCompletedSilently() {
      if (this.assignmentId === 'preview') return;

      // Prevent spamming the API if the SCORM package updates status multiple times
      if (this.alreadyMarkedComplete) return;
      this.alreadyMarkedComplete = true;

      try {
        await this.$axios.put(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/complete`);
      } catch (error) {
        console.warn('Silent fail: Could not mark SCORM as complete', error);
      }
    },

    async loadScormTrackingData() {
      try {
        const response = await this.$axios.get(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/scorm-tracking`);

        if (response.data && (response.data.id || response.data.ID)) {
          const loc = response.data.cmi_location || '';
          const sus = response.data.cmi_suspend_data || '';

          // TRAP: If they have a bookmark AND they are in Rewatch mode, pause and ask!
          if ((loc !== '' || sus !== '') && this.$route.query.rewatch) {
             this.tempDbTrackingData = response.data;
             this.showResumePrompt = true;
             return; // Stop here and wait for user input
          }

          // Otherwise, auto-resume normally
          this.applyTrackingToState(loc, sus, response.data.score);
        }

        // Launch if no prompt was needed
        this.launchScormIframe();
      } catch (error) {
        console.error('Error loading SCORM tracking data:', error);
        this.launchScormIframe(); // Fail-safe: launch anyway
      }
    },

    async handleResumeChoice(wantsToResume) {
      this.showResumePrompt = false;

      if (wantsToResume) {
        this.applyTrackingToState(
          this.tempDbTrackingData.cmi_location,
          this.tempDbTrackingData.cmi_suspend_data,
          this.tempDbTrackingData.score,
          this.tempDbTrackingData.status
        );
      } else {
        // 🚨 FIX FOR QA 3.1: START OVER completely wipes the local state
        this.applyTrackingToState('', '', '', 'incomplete');
        await this.saveScormTrackingData(this.cmiData);
      }

      this.launchScormIframe();
    },

    applyTrackingToState(loc, sus, score, status) {
        loc = loc || '';
        sus = sus || '';
        const safeStatus = status || 'incomplete';

        let scormScore = score || '';
        if (this.$route.query.rewatch) {
           scormScore = '';
        }

        this.cmiData['cmi.core.lesson_location'] = loc;
        this.cmiData['cmi.location'] = loc;
        this.cmiData['cmi.suspend_data'] = sus;

        this.cmiData['cmi.core.score.raw'] = scormScore;
        this.cmiData['cmi.score.raw'] = scormScore;

        this.cmiData['cmi.core.lesson_status'] = safeStatus;
        this.cmiData['cmi.completion_status'] = safeStatus;

        if (loc !== '' || sus !== '') {
          this.cmiData['cmi.core.entry'] = 'resume';
          this.cmiData['cmi.entry'] = 'resume';
        } else {
          this.cmiData['cmi.core.entry'] = 'ab-initio';
          this.cmiData['cmi.entry'] = 'ab-initio';
        }
    },

    launchScormIframe() {
      this.setupGlobalScormAPI();
      this.scormContentUrl = `/scorm_iframe/trainings/users/${this.userId}/modules/${this.moduleId}/scorm-content/${this.launchPath}`;
    },

    async saveScormTrackingData(cmiData) {
      if (this.assignmentId === 'preview') return;
      try {
        // Grab the status from whichever SCORM version is running (default to incomplete)
        const currentStatus = (
          cmiData['cmi.core.lesson_status'] ||
          cmiData['cmi.success_status'] ||
          cmiData['cmi.completion_status'] ||
          'incomplete'
        ).toLowerCase();

        const payload = {
          cmi_location: cmiData['cmi.core.lesson_location'] || cmiData['cmi.location'] || '',
          cmi_suspend_data: cmiData['cmi.suspend_data'] || '',
          score: parseFloat(cmiData['cmi.core.score.raw'] || cmiData['cmi.score.raw'] || 0),

          status: currentStatus
        };

        await this.$axios.post(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/scorm-tracking`, payload);
      } catch (error) {
        console.error('Error saving SCORM data:', error);
      }
    },

    async markInProgress() {
      if (this.assignmentId === 'preview') return;

      try {
        await this.$axios.put(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/status`, {
          status: 'in_progress'
        });
      } catch (error) {
        console.warn('Silent fail: Could not mark SCORM as in progress', error);
      }
    },

    setupGlobalScormAPI() {
      const self = this;
      let saveTimeout = null;

      const initFunction = () => {
        self.lmsInitialized = true;
        self.markInProgress();
        return "true";
      };

      const finishFunction = () => {
        self.lmsInitialized = false;

        self.saveScormTrackingData(self.cmiData).then(() => {
          if (self.cmiData['cmi.core.lesson_status'] === 'completed' ||
              self.cmiData['cmi.core.lesson_status'] === 'passed' ||
              self.cmiData['cmi.completion_status'] === 'completed' ||
              self.cmiData['cmi.success_status'] === 'passed') {

            self.markCompletedSilently();
            self.$emit('scormCompleted');
          }
        });

        return "true";
      };

      const getValue = (param) => {
        return self.cmiData[param] !== undefined ? String(self.cmiData[param]) : "";
      };

      const setValue = (param, value) => {

        // 2. MAX SCORE & BUGGY COMPLETION TRAP (Handles both 1.2 and 2004)
        if (param === 'cmi.core.score.raw' || param === 'cmi.score.raw' || param === 'cmi.score.scaled') {
          const oldScore = parseFloat(self.cmiData[param]) || 0;
          const newScore = parseFloat(value) || 0;

          if (newScore < oldScore) {
             return "true"; // Silently reject the lower score! Keep the higher one.
          }

          // SCORM 2004 Scaled Score (0.0 to 1.0) Fix
          if (param === 'cmi.score.scaled') {
            const translatedRaw = newScore * 100;
            self.cmiData['cmi.score.raw'] = translatedRaw;
            self.cmiData['cmi.core.score.raw'] = translatedRaw;

            if (translatedRaw >= 100) {
              self.cmiData['cmi.core.lesson_status'] = 'completed';
              self.cmiData['cmi.completion_status'] = 'completed';
              self.markCompletedSilently();
            }
          }
          // SCORM 1.2 & 2004 Raw Score (0 to 100) Fix
          else {
            if (newScore >= 100) {
              self.cmiData['cmi.core.lesson_status'] = 'completed';
              self.cmiData['cmi.completion_status'] = 'completed';
              self.markCompletedSilently();
            }
          }
        }

        // 3. NEW: STATUS DOWNGRADE TRAP
        if (param === 'cmi.core.lesson_status' || param === 'cmi.completion_status' || param === 'cmi.success_status') {
          const oldStatus = (self.cmiData[param] || '').toLowerCase();
          const valLower = String(value).toLowerCase();

          // If they already passed/completed it, ignore any attempts to make it incomplete/failed
          if ((oldStatus === 'completed' || oldStatus === 'passed') &&
              (valLower === 'incomplete' || valLower === 'failed' || valLower === 'browsed')) {
             return "true";
          }
        }

        // 4. Save the value to state (if it passed the traps)
        self.cmiData[param] = value;

        const valLower = String(value).toLowerCase();

        // 5. Standard Silent Completion Trap
        if (param === 'cmi.core.lesson_status' || param === 'cmi.completion_status' || param === 'cmi.success_status') {
          if (valLower === 'completed' || valLower === 'passed') {
            self.markCompletedSilently();
          }
          else if (valLower === 'failed') {
            self.cmiData['cmi.core.lesson_location'] = '';
            self.cmiData['cmi.location'] = '';
            self.cmiData['cmi.suspend_data'] = '';
          }
        }

        // 6. Debounced Auto-Save
        if (param === 'cmi.core.lesson_location' || param === 'cmi.location' || param === 'cmi.suspend_data' || param.includes('score') || param.includes('status') || param === 'cmi.progress_measure') {
          clearTimeout(saveTimeout);
          saveTimeout = setTimeout(() => {
            self.saveScormTrackingData(self.cmiData);
          }, 1000);
        }

        return "true";
      };

      const commitFunction = () => {
        self.saveScormTrackingData(self.cmiData);
        return "true";
      };

      window.API = {
        LMSInitialize: initFunction,
        LMSFinish: finishFunction,
        LMSGetValue: getValue,
        LMSSetValue: setValue,
        LMSCommit: commitFunction,
        LMSGetLastError: () => "0",
        LMSGetErrorString: () => "No error",
        LMSGetDiagnostic: () => "No diagnostic information"
      };

      window.API_1484_11 = {
        Initialize: initFunction,
        Terminate: finishFunction,
        GetValue: getValue,
        SetValue: setValue,
        Commit: commitFunction,
        GetLastError: () => "0",
        GetErrorString: () => "No error",
        GetDiagnostic: () => "No diagnostic information"
      };
    }
  }
};
</script>

<style scoped>
.scorm-player-container {
  width: 100%;
  height: 100%;
  min-height: 550px;
  background-color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.scorm-iframe {
  width: 100%;
  height: 100%;
  min-height: 550px;
  background-color: #ffffff;
  border: none;
}

.loading-state {
  text-align: center;
}

.resume-prompt-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(15, 23, 42, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.bg-theme {
  background-color: #00a0c1 !important;
}
</style>